import * as React from 'react';

import {GoogleMeetIcon} from 'shells/icon/svg/google-meet-icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {ModalHeader} from 'shells/typography/modal-header';

import type {MeetingUpdateInput} from 'nutshell-graphql-types';

import {authToService} from '../../../setup/integrations/auth-to-service';

import {ConnectButton} from './connect-button';
import {ConnectedWithReconfigureButton} from './connected-with-reconfigure-button';
import {DoneButton} from './done-button';
import {useGetMeetIntegration} from './graphql/hooks/use-get-meet-integration';
import {useSetGoogleMeetIsEnabled} from './graphql/hooks/use-set-google-meet-is-enabled';

import styles from './conferencing-modal-wrapper.module.css';

type Props = {
    meetingId: string;
    onSave: (arg1: MeetingUpdateInput) => void;
    closeModal: () => void;
    onFailToConnect?: () => void;
    windowFeatures: any;
};

export function GoogleMeetModalContent(props: Props) {
    const [error, setError] = React.useState<string | null | undefined>(null);

    const {googleMeetIntegration, refetch} = useGetMeetIntegration();
    const isMeetEnabled = googleMeetIntegration
        ? googleMeetIntegration.isEnabledCalendarSync
        : false;
    const setGoogleMeetIsEnabled = useSetGoogleMeetIsEnabled();

    const handleEnableGoogleMeet = () => {
        setGoogleMeetIsEnabled({isEnabled: true, meetAuthType: 'CALENDAR'}).then(
            (mutationResults) => {
                const authUrl =
                    mutationResults &&
                    mutationResults.data &&
                    mutationResults.data.setGoogleMeetIsEnabled
                        ? mutationResults.data.setGoogleMeetIsEnabled.authUrl
                        : null;

                if (authUrl) {
                    authToService({
                        authUrl,
                        windowFeatures: props.windowFeatures,
                        authCheckUrl: '/google/has-auth',
                    })
                        .then(({hasAuth}) => {
                            if (hasAuth) {
                                refetch();
                            }
                        })
                        .catch((errors) => setError(errors[0]));
                }
            }
        );
    };

    const handleReconfigureClick = () => {
        setGoogleMeetIsEnabled({isEnabled: false, meetAuthType: 'CALENDAR'}).then(() =>
            handleEnableGoogleMeet()
        );
    };

    const handleSave = () => {
        if (props.onSave) {
            props.onSave({
                id: props.meetingId,
                location: null,
                locationType: isMeetEnabled ? 'MEET' : undefined,
            });
        }
    };

    const handleClose = () => {
        if (!isMeetEnabled && props.onFailToConnect) {
            props.onFailToConnect();
        } else {
            props.closeModal();
        }
    };

    return (
        <div className={styles['content-container']}>
            <ModalHeader>Connect your Google Meet account</ModalHeader>
            <StackLegacy spacing={16}>
                <div>
                    Log in and authenticate with your Google account to automatically generate
                    meeting links whenever you select this option as your meeting location.
                </div>
                <div>
                    Virtual meeting links will be created and sent to all participants for each
                    booked meeting.
                </div>
                {isMeetEnabled ? (
                    <ConnectedWithReconfigureButton
                        integrationName='Google Meet'
                        onClick={handleReconfigureClick}
                    />
                ) : (
                    <ConnectButton
                        icon={GoogleMeetIcon}
                        integrationName='Google Meet'
                        onClick={handleEnableGoogleMeet}
                    />
                )}
                {error ? <div className={styles.error}>{error}</div> : undefined}
            </StackLegacy>
            {/* @ts-expect-error */}
            <DoneButton onSave={props.onSave ? handleSave : undefined} closeModal={handleClose} />
        </div>
    );
}
